const WHAT3WORDS_LINK_HOSTS = new Set([
  'what3words.com',
  'www.what3words.com',
  'w3w.co',
  'www.w3w.co',
]);
const MAX_WHAT3WORDS_INPUT_LENGTH = 255;
const WHAT3WORDS_WORD_PATTERN = /^\p{L}[\p{L}\p{M}]*$/u;

export type SubmittedWhat3wordsAddress =
  | { provided: false }
  | { provided: true; value: string | null }
  | { provided: true; error: string };

function decodeCandidate(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/**
 * Converts a pasted what3words address or supported share link into the
 * canonical database form: word.word.word (without the display slashes).
 *
 * This validates the shape only. Confirming that the location exists would
 * require the paid what3words API and is intentionally outside the first release.
 */
export function normalizeWhat3wordsAddress(input: string | null | undefined): string | null {
  if (input === null || input === undefined) return null;
  if (input.length > MAX_WHAT3WORDS_INPUT_LENGTH) return null;

  let candidate = input.trim();
  if (!candidate) return null;

  const lowercaseCandidate = candidate.toLowerCase();
  const looksLikeLink =
    /^https?:\/\//i.test(candidate) ||
    Array.from(WHAT3WORDS_LINK_HOSTS).some((host) =>
      lowercaseCandidate.startsWith(`${host}/`)
    );

  if (looksLikeLink) {
    try {
      const url = new URL(/^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`);
      if (!WHAT3WORDS_LINK_HOSTS.has(url.hostname.toLowerCase())) return null;

      candidate = url.pathname.split('/').filter(Boolean).at(-1) ?? '';
    } catch {
      return null;
    }
  }

  const decodedCandidate = decodeCandidate(candidate);
  if (decodedCandidate === null) return null;

  candidate = decodedCandidate
    .trim()
    .replace(/^\/{3}/, '')
    .replace(/\/+$/, '')
    .toLowerCase();

  if (candidate.length > MAX_WHAT3WORDS_INPUT_LENGTH) return null;

  const words = candidate.split('.');
  if (words.length !== 3 || words.some((word) => !WHAT3WORDS_WORD_PATTERN.test(word))) {
    return null;
  }

  return words.join('.');
}

export function formatWhat3wordsAddress(input: string | null | undefined): string | null {
  const normalized = normalizeWhat3wordsAddress(input);
  return normalized ? `///${normalized}` : null;
}

export function buildWhat3wordsMapUrl(input: string | null | undefined): string | null {
  const normalized = normalizeWhat3wordsAddress(input);
  return normalized
    ? `https://what3words.com/${normalized
        .split('.')
        .map((word) => encodeURIComponent(word))
        .join('.')}`
    : null;
}

export function readSubmittedWhat3wordsAddress(
  body: Record<string, unknown>
): SubmittedWhat3wordsAddress {
  const hasCamelCase = Object.prototype.hasOwnProperty.call(body, 'what3wordsAddress');
  const hasSnakeCase = Object.prototype.hasOwnProperty.call(body, 'what3words_address');

  if (!hasCamelCase && !hasSnakeCase) return { provided: false };

  const submittedValues = [
    ...(hasCamelCase ? [body.what3wordsAddress] : []),
    ...(hasSnakeCase ? [body.what3words_address] : []),
  ];
  let resolvedValue: string | null | undefined;

  for (const submittedValue of submittedValues) {
    let normalizedValue: string | null;

    if (
      submittedValue === null ||
      submittedValue === undefined ||
      (typeof submittedValue === 'string' && submittedValue.trim() === '')
    ) {
      normalizedValue = null;
    } else {
      if (typeof submittedValue !== 'string') {
        return {
          provided: true,
          error: 'Enter a what3words address in the format ///word.word.word.',
        };
      }

      normalizedValue = normalizeWhat3wordsAddress(submittedValue);
      if (!normalizedValue) {
        return {
          provided: true,
          error: 'Enter three dot-separated words or paste a what3words.com or w3w.co link.',
        };
      }
    }

    if (resolvedValue !== undefined && resolvedValue !== normalizedValue) {
      return {
        provided: true,
        error: 'Provide one what3words value, or make both submitted values match.',
      };
    }

    resolvedValue = normalizedValue;
  }

  return { provided: true, value: resolvedValue ?? null };
}
